import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, withErrorHandling, ApiError, getUser } from '../../api/client';
import { Button } from '../ui/Button/Button';
import { useEvaluationStore } from '../../store/evaluationStore';
import { formatDateTime } from '../../utils/dateFormatter';
import { ShareModal } from '../Collaboration/ShareModal';
import { evaluationApi } from '../../api/evaluation';
import { LinkIcon, UsersIcon, CopyIcon, MoreVerticalIcon, TrashIcon } from '../Icons/Icons';
import { LoadingSpinner } from '../ui/LoadingSpinner/LoadingSpinner';
import { ProjectMergeWizard } from '../ProjectMerge/ProjectMergeWizard';
import { useThemeColors } from '../../hooks/useThemeColors';
import type { DashboardResponse, ExaminationSummary, EvaluationStatus } from '../../types';
import './Dashboard.css';

interface DashboardProps {
  onStartEvaluation: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onStartEvaluation }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const colors = useThemeColors();
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'inProgress' | 'completed'>('inProgress');
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // 共有モーダルの状態
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedExamination, setSelectedExamination] = useState<ExaminationSummary | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // プロジェクトマージモーダルの状態
  const [mergeWizardOpen, setMergeWizardOpen] = useState(false);

  const {
    resetAll,
    setCurrentStep,
    products,
    criteria,
    currentStep,
    loadDataAsync,
    deleteDataAsync,
    currentExaminationId,
    setCurrentExaminationId,
    evaluationTitle,
    setEvaluationTitle
  } = useEvaluationStore();

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (openMenuId && !target.closest('.dropdown-menu') && !target.closest('.menu-button')) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  const loadDashboard = async () => {
    setIsLoading(true);
    setError(null);

    const result = await withErrorHandling(
      () => apiClient.getDashboard(),
      (error: ApiError) => {
        console.error('Dashboard load error:', error);
        // 401エラーはfetchInterceptorで処理されるため、ここでは処理不要
        if (error.message.includes('502') || error.message.includes('Failed to fetch')) {
          setError(`${t('error.dashboardLoad')}: ${t('error.serverUnavailable')}`);
        } else {
          setError(`${t('error.dashboardLoad')}: ${error.message}`);
        }
      }
    );

    setIsLoading(false);

    if (result) {
      setDashboardData(result);
    }
  }; const handleCreateNew = async () => {
    setError(null);

    // Check evaluation creation limit
    try {
      const limitStatus = await evaluationApi.getEvaluationLimitStatus();

      if (!limitStatus.can_create) {
        const message = limitStatus.max_count
          ? t('dashboard.projectLimitReached', { count: limitStatus.max_count })
          : t('dashboard.cannotCreateProject');
        alert(message);
        return;
      }

      // If within limits, show remaining count
      if (limitStatus.max_count && limitStatus.remaining !== null && limitStatus.remaining === 1) {
        alert(t('dashboard.freeLastProject'));
      }
    } catch (error) {
      console.error('Failed to check evaluation limit:', error);
      setError(t('dashboard.evaluationLimitCheckFailed'));
      return;
    }

    // プロジェクト名の入力を求める
    const projectName = prompt(t('dashboard.enterProjectName'));
    
    // キャンセルされた場合は処理を中断
    if (projectName === null) {
      return;
    }

    // 新しい評価を開始（データはクリア）
    resetAll();
    setCurrentExaminationId(null); // 現在の評価IDもクリア
    setCurrentStep(1); // 最初のステップから開始

    // 入力されたタイトルを設定（空の場合はデフォルトタイトル）
    const { setEvaluationTitle } = useEvaluationStore.getState();
    const title = projectName.trim() || t('dashboard.untitledProject');
    setEvaluationTitle(title);

    // 評価作業画面に遷移（新規作成の場合はIDなしのURLで）
    const user = getUser();
    const userId = user?.id || 'unknown';
    navigate(`/evaluation/${userId}/new`);
  };
  const handleLoadExamination = async (examinationId: string) => {
    setError(null);

    // 評価IDを設定しない（EvaluationPageで設定される）
    // setCurrentExaminationId(examinationId);
    
    // ユーザー情報を取得（ユーザーIDのため）
    const user = getUser();
    const userId = user?.id || 'unknown';
    
    // 新しいルーティング構造でナビゲート
    navigate(`/evaluation/${userId}/${examinationId}`);
  }; const handleCopyExamination = async (examination: ExaminationSummary) => {
    // Check evaluation creation limit before showing prompt
    try {
      const limitStatus = await evaluationApi.getEvaluationLimitStatus();

      if (!limitStatus.can_create) {
        const message = limitStatus.max_count
          ? t('dashboard.projectLimitReached', { count: limitStatus.max_count })
          : t('dashboard.cannotCreateProject');
        alert(message);
        return;
      }
    } catch (error) {
      console.error('Failed to check evaluation limit:', error);
      setError(t('dashboard.evaluationLimitCheckFailed'));
      return;
    }

    const newTitle = prompt(
      t('dashboard.copyProjectTitle', { title: examination.summaryTitle || t('status.untitled') }) + '\n' + t('dashboard.copyProjectPrompt')
    );

    if (newTitle === null) {
      return; // キャンセルされた場合
    }

    setError(null);
    setIsLoading(true);

    try {
      const result = await withErrorHandling(
        () => apiClient.copyExamination(examination.summaryId, {
          title: newTitle || undefined,
          copyProducts: true,
          copyCriteria: true,
          copyGrades: true,
          copyEvaluations: false // 評価位置はデフォルトではコピーしない
        }),
        (error: ApiError) => setError(t('dashboard.copyError', { message: error.message }))
      );

      if (result) {
        // ダッシュボードを再読み込み
        await loadDashboard();
        console.log(`評価プロジェクト「${examination.summaryTitle}」をコピーしました`);
      }
    } catch (error) {
      console.error('コピー処理でエラー:', error);
      setError(t('dashboard.copyProcessError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteExamination = async (examinationId: string, title?: string) => {
    if (!confirm(t('dashboard.deleteConfirm', { title: title || t('status.untitled') }))) {
      return;
    }

    setError(null);
    setIsLoading(true);

    console.log(`削除開始: ${examinationId} (${title})`);

    try {
      // 特定の評価プロジェクトを削除
      const result = await withErrorHandling(
        () => apiClient.deleteExamination(examinationId),
        (error: ApiError) => setError(t('dashboard.deleteError', { message: error.message }))
      );

      console.log('削除API結果:', result);

      if (result !== null) {
        // 現在の評価が削除された場合はリセット
        if (currentExaminationId === examinationId) {
          console.log('現在の評価が削除されたため、ストアをリセット');
          resetAll();
          setCurrentExaminationId(null);
        }

        // ダッシュボードを再読み込み
        console.log('ダッシュボードを再読み込み中...');
        await loadDashboard();
        console.log('ダッシュボード再読み込み完了');

        // 成功メッセージ
        console.log(`評価プロジェクト「${title}」を削除しました`);
      } else {
        // エラーメッセージが設定されていない場合のフォールバック
        if (!error) {
          setError(t('error.deleteFailed'));
        }
      }
    } catch (error) {
      console.error('削除処理でエラー:', error);
      setError(t('dashboard.deleteProcessError'));
    } finally {
      setIsLoading(false);
    }
  };
  const getStatusColor = (status: EvaluationStatus): string => {
    switch (status) {
      case 'InProgress': return colors.info;
      case 'Completed': return colors.success;
      default: return colors.textSecondary;
    }
  };

  const getStatusText = (status: EvaluationStatus): string => {
    switch (status) {
      case 'InProgress': return t('status.inProgress');
      case 'Completed': return t('status.completed');
      default: return t('dashboard.statusUnknown');
    }
  };

  // Use the locale-aware date formatter from utils
  const formatDate = formatDateTime;
  // 共有モーダル関連の処理
  const handleOpenShareModal = (examination: ExaminationSummary) => {
    setSelectedExamination(examination);
    setShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
    setSelectedExamination(null);
  };
  const handleSharingToggle = async (enabled: boolean) => {
    if (selectedExamination) {
      // 共有設定の変更をダッシュボードに反映
      try {
        // 直接選択中の試験情報も更新しておく
        setSelectedExamination({
          ...selectedExamination,
          isShared: enabled
        });

        // ダッシュボード全体を再読込
        await loadDashboard();
      } catch (error) {
        console.error('共有設定更新エラー:', error);
        setError(t('dashboard.shareSettingsUpdateFailed'));
      }
    }
  };

  const handleMenuToggle = (examinationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenMenuId(openMenuId === examinationId ? null : examinationId);
  };

  const handleMenuAction = (event: React.MouseEvent, action: () => void) => {
    event.stopPropagation();
    action();
    setOpenMenuId(null);
  };

  const renderExaminationCard = (examination: ExaminationSummary) => (
    <div
      key={examination.summaryId}
      className={`examination-card ${openMenuId === examination.summaryId ? 'menu-open' : ''}`}
      onClick={() => {
        if (!openMenuId) {
          handleLoadExamination(examination.summaryId);
        } else {
          setOpenMenuId(null);
        }
      }}
    >
      <div className="examination-card__header">        <div className="examination-card__title-section">
        <h3 className="examination-card__title">
          {examination.summaryTitle || t('status.untitled')}
          {examination.isShared && (
            <span className="shared-indicator" title={t('dashboard.sharing')}>
              <LinkIcon />
            </span>
          )}
        </h3>
        <div className="examination-card__meta">
          <span
            className="examination-card__status"
            style={{ backgroundColor: getStatusColor(examination.summaryStatus) }}
          >
            {getStatusText(examination.summaryStatus)}
          </span>
          {examination.collaboratorCount && examination.collaboratorCount > 0 && (
            <span className="collaborator-count" title={t('dashboard.collaborators', { count: examination.collaboratorCount })}>
              <UsersIcon />
              <span>{examination.collaboratorCount}</span>
            </span>
          )}
        </div>
      </div>
        <div className="examination-card__actions">
          <div className="dropdown-container">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => handleMenuToggle(examination.summaryId, e)}
              title={t('dashboard.menu')}
              className="icon-button menu-button"
            >
              <MoreVerticalIcon />
            </Button>
            {openMenuId === examination.summaryId && (
              <div className="dropdown-menu">
                <button
                  className="dropdown-item"
                  onClick={(e) => handleMenuAction(e, () => handleOpenShareModal(examination))}
                >
                  <LinkIcon />
                  <span>{t('dashboard.shareSettings')}</span>
                </button>
                <button
                  className="dropdown-item"
                  onClick={(e) => handleMenuAction(e, () => handleCopyExamination(examination))}
                >
                  <CopyIcon />
                  <span>{t('dashboard.copy')}</span>
                </button>
                <div className="dropdown-divider" />
                <button
                  className="dropdown-item dropdown-item--danger"
                  onClick={(e) => handleMenuAction(e, () => handleDeleteExamination(examination.summaryId, examination.summaryTitle))}
                >
                  <TrashIcon />
                  <span>{t('dashboard.delete')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="examination-card__content">
        <div className="examination-card__stats">
          <div className="stat">
            <span className="stat__label">{t('dashboard.subjects')}</span>
            <span className="stat__value">{examination.summaryProductCount}件</span>
          </div>
          <div className="stat">
            <span className="stat__label">{t('dashboard.criteria')}</span>
            <span className="stat__value">{examination.summaryCriteriaCount}件</span>
          </div>
          <div className="stat">
            <span className="stat__label">{t('dashboard.progress')}</span>
            <span className="stat__value">
              {examination.summaryProgress.progressPercentage.toFixed(0)}%
            </span>
          </div>
        </div>

        {examination.summaryProgress.progressTotal > 0 && (
          <div className="examination-card__progress">
            <div className="progress-bar">
              <div
                className="progress-bar__fill"
                style={{
                  width: `${examination.summaryProgress.progressPercentage}%`,
                  backgroundColor: getStatusColor(examination.summaryStatus)
                }}
              />
            </div>
            <span className="progress-text">
              {examination.summaryProgress.progressCompleted} / {examination.summaryProgress.progressTotal} {t('dashboard.completed')}
            </span>
          </div>
        )}

        <div className="examination-card__dates">
          <span className="date-info">
            {t('dashboard.created')} {formatDate(examination.summaryCreatedAt)}
          </span>
          <span className="date-info">
            {t('dashboard.updated')} {formatDate(examination.summaryUpdatedAt)}
          </span>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="dashboard">
        <div className="dashboard__loading">
          <LoadingSpinner size="lg" />
          <p>{t('dashboard.loadingDashboard')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="dashboard__error">
          <p>{error}</p>
          <Button onClick={loadDashboard}>{t('dashboard.retry')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <h1>{t('dashboard.title')}</h1>
        <div className="header-actions">
          {/* プロジェクト統合機能を一時的に無効化
          <Button
            variant="ghost"
            onClick={() => setMergeWizardOpen(true)}
          >
            {t('dashboard.projectMerge')}
          </Button>
          */}
          <Button
            variant="primary"
            onClick={handleCreateNew}
          >
            {t('dashboard.createNew')}
          </Button>
        </div>
      </div>

      <div className="dashboard__tabs">
        <button
          className={`tab ${activeTab === 'inProgress' ? 'tab--active' : ''}`}
          onClick={() => setActiveTab('inProgress')}
        >
          {t('dashboard.inProgressTab')} ({dashboardData?.inProgressExaminations.length || 0})
        </button>
        <button
          className={`tab ${activeTab === 'completed' ? 'tab--active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          {t('dashboard.completedTab')} ({dashboardData?.completedExaminations.length || 0})
        </button>
      </div>

      <div className="dashboard__content">
        {activeTab === 'inProgress' && (
          <div className="examinations-grid">
            {dashboardData?.inProgressExaminations.length === 0 ? (
              <div className="empty-state">
                <h2>{t('dashboard.noInProgress')}</h2>
                <p>{t('dashboard.noInProgressDesc')}</p>
                <Button
                  variant="primary"
                  onClick={handleCreateNew}
                >
                  {t('dashboard.createNew')}
                </Button>
              </div>
            ) : (
              dashboardData?.inProgressExaminations.map(renderExaminationCard)
            )}
          </div>
        )}        {activeTab === 'completed' && (
          <div className="examinations-grid">
            {dashboardData?.completedExaminations.length === 0 ? (
              <div className="empty-state">
                <h2>{t('dashboard.noCompleted')}</h2>
                <p>{t('dashboard.noCompletedDesc')}</p>
              </div>
            ) : (
              dashboardData?.completedExaminations.map(renderExaminationCard)
            )}
          </div>
        )}
      </div>      {shareModalOpen && selectedExamination && (
        <ShareModal
          examinationId={selectedExamination.summaryId}
          examinationTitle={selectedExamination.summaryTitle || t('status.untitled')}
          isShared={selectedExamination.isShared || false}
          currentUserRole={selectedExamination.currentUserRole || 'owner'}
          onClose={handleCloseShareModal}
          onSharingToggle={handleSharingToggle}
        />
      )}

      {mergeWizardOpen && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setMergeWizardOpen(false)}>
          <div className="modal-content">
            <ProjectMergeWizard
              onComplete={() => {
                setMergeWizardOpen(false);
                onStartEvaluation();
              }}
              onCancel={() => setMergeWizardOpen(false)}
            />
          </div>
        </div>
      )}

      <style>{`
        .dashboard {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }

        .dashboard__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .dashboard__header h1 {
          font-size: 1.5rem;
          color: #1f2937;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .dashboard-auto-save {
          min-width: auto;
          border-radius: 6px;
          font-size: 13px;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: flex-start;
          justify-content: center;
          z-index: 9999;
          padding: 2rem;
          overflow-y: auto;
        }

        .modal-content {
          background: white;
          border-radius: 0.75rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          max-height: 90vh;
          overflow-y: auto;
          margin-top: 2rem;
          width: 100%;
          max-width: 1000px;
        }

        .dashboard__loading,
        .dashboard__error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem;
          text-align: center;
          min-height: 60vh; /* 高さを確保 */
        }


        .dashboard__tabs {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 2rem;
          border-bottom: 2px solid #e5e7eb;
        }

        .tab {
          padding: 0.75rem 1.5rem;
          border: none;
          background: none;
          font-size: 1rem;
          color: #6b7280;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
        }

        .tab:hover {
          color: #374151;
          background-color: #f9fafb;
        }

        .tab--active {
          color: ${colors.infoLight};
          border-bottom-color: ${colors.infoLight};
          font-weight: 600;
        }

        .examinations-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 1.5rem;
        }

        .examination-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          padding: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.2s;
          cursor: pointer;
          position: relative;
        }

        .examination-card:hover:not(.menu-open) {
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transform: translateY(-1px);
        }
        
        .examination-card.menu-open {
          z-index: 100;
        }

        .examination-card__header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
        }

        .examination-card__title-section {
          flex: 1;
        }        .examination-card__title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
          line-height: 1.3;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .shared-indicator {
          display: inline-flex;
          align-items: center;
          opacity: 0.7;
        }

        .shared-indicator svg {
          width: 1rem;
          height: 1rem;
        }

        .examination-card__meta {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .examination-card__status {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .collaborator-count {
          font-size: 0.75rem;
          color: #6b7280;
          background: #f3f4f6;
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .collaborator-count svg {
          width: 0.875rem;
          height: 0.875rem;
        }

        .icon-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .icon-button svg {
          width: 1.125rem;
          height: 1.125rem;
        }

        .examination-card__actions {
          position: relative;
        }

        .dropdown-container {
          position: relative;
        }

        .menu-button {
          padding: 0.5rem;
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 0.25rem;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          min-width: 180px;
          z-index: 9999;
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-0.5rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 0.75rem;
          width: 100%;
          padding: 0.75rem 1rem;
          text-align: left;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 0.875rem;
          color: #374151;
          transition: background-color 0.2s;
        }

        .dropdown-item:hover {
          background-color: #f3f4f6;
        }

        .dropdown-item svg {
          width: 1rem;
          height: 1rem;
          flex-shrink: 0;
        }

        .dropdown-item--danger {
          color: #dc2626;
        }

        .dropdown-item--danger:hover {
          background-color: #fee2e2;
        }

        .dropdown-divider {
          height: 1px;
          background-color: #e5e7eb;
          margin: 0.25rem 0;
        }

        .examination-card__content {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .examination-card__stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }

        .stat {
          text-align: center;
        }

        .stat__label {
          display: block;
          font-size: 0.75rem;
          color: #6b7280;
          margin-bottom: 0.25rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .stat__value {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
        }

        .examination-card__progress {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .progress-bar {
          height: 0.5rem;
          background-color: #e5e7eb;
          border-radius: 0.25rem;
          overflow: hidden;
        }

        .progress-bar__fill {
          height: 100%;
          transition: width 0.3s ease;
        }

        .progress-text {
          font-size: 0.875rem;
          color: #6b7280;
          text-align: center;
        }

        .examination-card__dates {
          display: flex;
          justify-content: space-between;
          padding-top: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .date-info {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .empty-state {
          grid-column: 1 / -1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem;
          text-align: center;
          background: #f9fafb;
          border: 2px dashed #d1d5db;
          border-radius: 0.75rem;
        }

        .empty-state h2 {
          font-size: 1.5rem;
          color: #374151;
          margin: 0 0 0.5rem 0;
        }

        .empty-state p {
          color: #6b7280;
          margin: 0 0 1.5rem 0;
        }

        @media (max-width: 768px) {
          .dashboard {
            padding: 1rem;
          }

          .dashboard__header {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }

          .examinations-grid {
            grid-template-columns: 1fr;
          }

          .examination-card__header {
            flex-direction: column;
            gap: 1rem;
          }

          .examination-card__actions {
            margin-left: 0;
            justify-content: flex-end;
          }

          .examination-card__stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .examination-card__dates {
            flex-direction: column;
            gap: 0.25rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;